import api from './api';

export const authService = {
  // تسجيل الدخول
  async login(credentials) {
    try {
      const response = await api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password
      });
      
      if (response.data.success) {
        const responseData = response.data.data;
        const { user, access_token, refresh_token } = responseData;
        
        // حفظ التوكن في localStorage
        localStorage.setItem('authToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        
        return {
          success: true,
          user: {
            id: user.id,
            username: user.username,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            name: `${user.firstName} ${user.lastName}`,
            isVerified: user.isVerified,
            isActive: user.isActive,
            lastLogin: user.lastLogin,
            // تحديد الدور بناءً على username
            role: this.determineUserRole(user.username)
          },
          token: access_token
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Network error'
      };
    }
  },

  // تحديد دور المستخدم بناءً على username
  determineUserRole(username) {
    if (!username || typeof username !== 'string') {
      console.warn('Username is undefined or not a string:', username);
      return 'STUDENT';
    }
    
    const roleMap = {
      'superadmin': 'SUPER_ADMIN',
      'admin': 'ADMIN',
      'manager': 'MANAGER',
      'supervisor': 'SUPERVISOR',
      'trainer': 'TRAINER',
      'student': 'STUDENT'
    };
    
    return roleMap[username.toLowerCase()] || 'STUDENT';
  },

  // تحديد دور المستخدم من قائمة الأدوار
  determineUserRoleFromRoles(roles, username) {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      // إذا لم تكن هناك أدوار، نعود للطريقة القديمة
      return this.determineUserRole(username);
    }
    
    // البحث عن أعلى دور (أعلى level)
    const sortedRoles = roles.sort((a, b) => (b.level || 0) - (a.level || 0));
    const highestRole = sortedRoles[0];
    
    if (highestRole && highestRole.name) {
      const roleMap = {
        'super_admin': 'SUPER_ADMIN',
        'admin': 'ADMIN',
        'manager': 'MANAGER',
        'supervisor': 'SUPERVISOR',
        'trainer': 'TRAINER',
        'student': 'STUDENT'
      };
      
      return roleMap[highestRole.name.toLowerCase()] || 'STUDENT';
    }
    
    // إذا فشل كل شيء، نعود للطريقة القديمة
    return this.determineUserRole(username);
  },

  // تسجيل الخروج
  async logout() {
    try {
      // محاولة إرسال طلب logout للخادم
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // حذف التوكنات من localStorage في جميع الأحوال
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
  },

  // الحصول على بيانات المستخدم الحالي
  async getCurrentUser() {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await api.get('/auth/me');
      
      if (response.data.success) {
        const responseData = response.data.data;
        
        // البيانات تأتي في responseData.user
        const user = responseData.user;
        const roles = responseData.roles || [];
        const organizationalAccess = responseData.organizationalAccess || responseData.organizational_access || responseData.user?.organizationalAccess || responseData.user?.organizational_access || [];
        
        // التحقق من وجود البيانات المطلوبة
        if (!user || !user.username) {
          console.warn('User data is incomplete:', responseData);
          throw new Error('Incomplete user data');
        }
        
        // تحديد الدور من قائمة الأدوار
        const userRole = this.determineUserRoleFromRoles(roles, user.username);
        
        const permissions = responseData.permissions || [];
        const level = responseData.level || null;
        return {
          id: user.id,
          username: user.username,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          name: `${user.firstName} ${user.lastName}`,
          isVerified: user.isVerified,
          isActive: user.isActive,
          lastLogin: user.lastLogin,
          role: userRole,
          roles: roles,
          permissions: Array.isArray(permissions) ? permissions : [],
          organizationalAccess: organizationalAccess,
          level: level ? { levelId: level.levelId, levelNumber: level.levelNumber, levelName: level.levelName } : null
        };
      } else {
        throw new Error('Failed to get user data');
      }
    } catch (error) {
      console.error('Error getting current user:', error);
      
      // حذف التوكنات فقط في حالة 401 (انتهاء الصلاحية)
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
      }
      
      throw error;
    }
  },

  // تحديث كلمة المرور
  async changePassword(passwordData) {
    try {
      const response = await api.put('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      console.error('خطأ في تغيير كلمة المرور:', error);
      throw error;
    }
  },

  // تحديث الملف الشخصي
  async updateProfile(profileData) {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('خطأ في تحديث الملف الشخصي:', error);
      throw error;
    }
  },

  // تحديث اللغة المفضلة
  async updateLanguage(language) {
    try {
      const response = await api.put('/auth/language', { language });
      return response.data;
    } catch (error) {
      console.error('خطأ في تحديث اللغة:', error);
      throw error;
    }
  }
};
